import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from '../common';
import { PodcastFeedSummary } from '../../types';
import { podcastFeedsApi } from '../../api/podcastFeeds';

interface AddToFeedModalProps {
  isOpen: boolean;
  conversionIds: string[];
  onClose: () => void;
  onAdded: (feed: PodcastFeedSummary) => void;
}

export default function AddToFeedModal({
  isOpen,
  conversionIds,
  onClose,
  onAdded,
}: AddToFeedModalProps) {
  const [feeds, setFeeds] = useState<PodcastFeedSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyFeedId, setBusyFeedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLoading(true);
    setCreatingNew(false);
    setNewName('');
    setNewDescription('');
    podcastFeedsApi
      .list()
      .then((response) => setFeeds(response.items))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load feeds'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const addToExisting = async (feed: PodcastFeedSummary) => {
    setBusyFeedId(feed.id);
    setError(null);
    try {
      const updated = await podcastFeedsApi.addItems(feed.id, conversionIds);
      onAdded({
        ...feed,
        itemCount: updated.itemCount,
        updatedAt: updated.updatedAt,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to feed');
    } finally {
      setBusyFeedId(null);
    }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) {
      setError('Feed name is required');
      return;
    }
    setBusyFeedId('__new__');
    setError(null);
    try {
      const created = await podcastFeedsApi.create({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        conversionIds,
        published: false,
      });
      onAdded({
        id: created.id,
        userId: created.userId,
        name: created.name,
        description: created.description,
        published: created.published,
        feedToken: created.feedToken,
        feedUrl: created.feedUrl,
        itemCount: created.itemCount,
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create feed');
    } finally {
      setBusyFeedId(null);
    }
  };

  const count = conversionIds.length;
  const countLabel = `${count} conversion${count === 1 ? '' : 's'}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (busyFeedId == null) onClose();
      }}
      title={`Add ${countLabel} to a feed`}
    >
      {error && <p className="font-body text-sm text-destructive mb-4">{error}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : creatingNew ? (
        <div className="space-y-4">
          <div>
            <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
              Name
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={200}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-body text-sm text-foreground"
            />
          </div>
          <div>
            <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
              Description
            </label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-body text-sm text-foreground"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setCreatingNew(false)}
              disabled={busyFeedId !== null}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={createAndAdd}
              disabled={busyFeedId !== null}
            >
              {busyFeedId === '__new__' ? 'Creating…' : 'Create and add'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {feeds.length === 0 ? (
            <p className="font-body text-sm text-muted-foreground py-2">
              You don't have any feeds yet. Create your first one below.
            </p>
          ) : (
            <ul className="space-y-2">
              {feeds.map((feed) => (
                <li
                  key={feed.id}
                  className="flex items-center justify-between gap-3 p-3 border border-border rounded-sm"
                >
                  <div className="min-w-0">
                    <p className="font-body text-sm font-medium text-foreground truncate">
                      {feed.name}
                    </p>
                    <p className="font-mono text-xs tracking-wide text-muted-foreground">
                      {feed.itemCount} episode{feed.itemCount === 1 ? '' : 's'}
                      {feed.published ? ' · Published' : ' · Draft'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => addToExisting(feed)}
                    disabled={busyFeedId !== null}
                  >
                    {busyFeedId === feed.id ? 'Adding…' : 'Add'}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-2 flex justify-end">
            <Button variant="secondary" onClick={() => setCreatingNew(true)}>
              Create new feed
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
